import { useState, useEffect, useRef } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useMinimumLoadingTime from '@/hooks/useMinimumLoadingTime';
import BannerSection from '@/sections/home/BannerSection';
import RecommendationCardsSection from '@/sections/home/RecommendationCardsSection';
import HotBrandsSection from '@/sections/home/HotBrandsSection';
import HotCardsSection from '@/sections/home/HotCardsSection';
import { getBrands, getCards, getFeaturedProducts, getHeroBanners } from '@/api/products';
import usePenaltyStatus from '@/hooks/usePenaltyStatus';
import PenaltyRestrictionDialog from '@/components/PenaltyRestrictionDialog';
import useAuthStore from '@/stores/authStore';
import { PENALTY_REMINDER_LAST_SHOWN_KEY } from '@/config/storageKeys';
import { openPenaltyPaymentModal } from '@/stores/penaltyPaymentStore';

const HOME_FILTER_STATE_KEY = 'home_filter_state';

const getStoredFilters = () => {
  try {
    const cached = sessionStorage.getItem(HOME_FILTER_STATE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        brand: parsed.brand || 'ALL',
        category: parsed.category || 'ALL',
        sort:
          parsed.sort?.field && parsed.sort?.direction
            ? parsed.sort
            : { field: 'POPULARITY', direction: 'DESC' },
      };
    }
  } catch (error) {
    console.warn('Failed to parse home filter cache', error);
    sessionStorage.removeItem(HOME_FILTER_STATE_KEY);
  }

  return {
    brand: 'ALL',
    category: 'ALL',
    sort: { field: 'POPULARITY', direction: 'DESC' },
  };
};

const clearHomeFilterState = () => {
  try {
    sessionStorage.removeItem(HOME_FILTER_STATE_KEY);
    const recommendationKeys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('recommendation_cards_')) {
        recommendationKeys.push(key);
      }
    }
    recommendationKeys.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear home filter cache', error);
  }
};

const HomePage = () => {
  const { t } = useTranslation();
  const initialFilters = useRef(getStoredFilters());
  // Clear filter cache on full reload
  useEffect(() => {
    const navigationEntries = performance?.getEntriesByType?.('navigation');
    const isReloadNavigation =
      navigationEntries?.[0]?.type === 'reload' || performance?.navigation?.type === 1;

    if (isReloadNavigation) {
      sessionStorage.removeItem(HOME_FILTER_STATE_KEY);
    }
  }, []);

  const [selectedBrand, setSelectedBrand] = useState(initialFilters.current.brand);
  const [selectedCategory, setSelectedCategory] = useState(initialFilters.current.category);
  const [sortConfig, setSortConfig] = useState(initialFilters.current.sort);
  const [isPenaltyModalOpen, setPenaltyModalOpen] = useState(false);
  const [penaltyDialogMode, setPenaltyDialogMode] = useState('penalty');
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const {
    hasOutstandingPenalties,
    isBanned,
    isFetched: penaltyStatusFetched,
    isFetching: penaltyStatusFetching,
  } = usePenaltyStatus();
  // Fetch hero banners using React Query
  const { data: bannersData, isLoading: isBannersLoading } = useQuery({
    queryKey: ['heroBanners'],
    queryFn: getHeroBanners,
    select: data => data.data.map(banner => ({
      image: banner.image.url,
      title: banner.title,
      action: banner.action
    })),
  });

  // Fetch brands using React Query
  const { data: brandsData, isLoading: isBrandsLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    select: data => data.data, // Accessing the data field from the API response
  });

  // Fetch featured products using React Query
  const { data: featuredProductsData, isLoading: isFeaturedProductsLoading } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: getFeaturedProducts,
    select: data => data.data, // Accessing the data field from the API response
  });

  // Fetch recommended cards using React Query with infinite pagination
  const {
    data: cardsData,
    isLoading: isCardsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['cards', selectedCategory, sortConfig, selectedBrand],
    queryFn: ({ pageParam = null }) => getCards({
      ...(selectedCategory !== 'ALL' && { type: selectedCategory }),
      sort: sortConfig.field,
      direction: sortConfig.direction,
      ...(selectedBrand !== 'ALL' && { brandId: selectedBrand }),
      limit: 40,
      cursor: pageParam
    }),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: null,
  });

  // Persist filters whenever they change
  useEffect(() => {
    const filtersToCache = {
      brand: selectedBrand,
      category: selectedCategory,
      sort: sortConfig,
    };
    try {
      sessionStorage.setItem(HOME_FILTER_STATE_KEY, JSON.stringify(filtersToCache));
    } catch (error) {
      console.warn('Failed to persist home filters', error);
    }
  }, [selectedBrand, selectedCategory, sortConfig]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearHomeFilterState();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleBrandChange = (brandId) => {
    setSelectedBrand(brandId);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSortChange = (sortItem, direction) => {
    // Map the sort values to API expected values
    const sortMapping = {
      'date': 'RELEASE_DATE',
      'price': 'LOWEST_PRICE',
      'popularity': 'POPULARITY'
    };

    setSortConfig({
      field: sortMapping[sortItem.value],
      direction: direction.toUpperCase()
    });
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Track the number of pages loaded
  const pagesLoaded = cardsData?.pages?.length || 0;

  // Flatten the paginated data into a single array
  const allCards = cardsData?.pages?.flatMap(page => page.data) || [];

  // Apply minimum loading time to all loading states
  const showBannersLoading = useMinimumLoadingTime(isBannersLoading);
  const showBrandsLoading = useMinimumLoadingTime(isBrandsLoading);
  const showFeaturedProductsLoading = useMinimumLoadingTime(isFeaturedProductsLoading);
  const showCardsLoading = useMinimumLoadingTime(isCardsLoading) || (isCardsLoading && allCards.length === 0);

  // Convert brands data for Select component
  const brandOptions = [
    { value: 'ALL', label: t('home.filters.brands.all') },
    ...(brandsData?.map(brand => ({
      value: brand.id,
      label: brand.localizedName
    })) || [])
  ];

  // Category options
  const categoryOptions = [
    { value: 'ALL', label: t('home.filters.categories.all') },
    { value: 'SINGLE_CARD', label: t('home.filters.categories.single') },
    { value: 'CARD_BOX', label: t('home.filters.categories.box') },
  ];

  useEffect(() => {
    if (!accessToken || !user?.id) {
      setPenaltyModalOpen(false);
      return;
    }

    if (!penaltyStatusFetched || penaltyStatusFetching) {
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    let record = {};

    try {
      const raw = localStorage.getItem(PENALTY_REMINDER_LAST_SHOWN_KEY);
      if (raw) {
        record = JSON.parse(raw) || {};
      }
    } catch (error) {
      console.warn('Failed to parse penalty reminder history', error);
    }

    const userRecordRaw = record[user.id];
    const userRecord =
      typeof userRecordRaw === 'string'
        ? { penalty: userRecordRaw }
        : userRecordRaw || {};

    if (isBanned) {
      setPenaltyDialogMode('banned');

      if (userRecord.banned === today) {
        setPenaltyModalOpen(false);
        return;
      }

      const updatedRecord = {
        ...record,
        [user.id]: {
          ...userRecord,
          banned: today,
        },
      };

      try {
        localStorage.setItem(
          PENALTY_REMINDER_LAST_SHOWN_KEY,
          JSON.stringify(updatedRecord)
        );
      } catch (error) {
        console.warn('Failed to persist penalty reminder history', error);
      }

      setPenaltyModalOpen(true);
      return;
    }

    if (!hasOutstandingPenalties) {
      setPenaltyModalOpen(false);
      return;
    }

    if (userRecord.penalty === today) {
      return;
    }

    setPenaltyDialogMode('penalty');

    const updatedRecord = {
      ...record,
      [user.id]: {
        ...userRecord,
        penalty: today,
      },
    };

    try {
      localStorage.setItem(
        PENALTY_REMINDER_LAST_SHOWN_KEY,
        JSON.stringify(updatedRecord)
      );
    } catch (error) {
      console.warn('Failed to persist penalty reminder history', error);
    }

    setPenaltyModalOpen(true);
  }, [accessToken, user?.id, hasOutstandingPenalties, isBanned, penaltyStatusFetched, penaltyStatusFetching]);

  const handlePenaltyConfirm = () => {
    setPenaltyModalOpen(false);
    openPenaltyPaymentModal();
  };

  const handlePenaltyDismiss = () => {
    setPenaltyModalOpen(false);
  };

  return (
    <>
      <div className="container pb-8 mt-6">
        <BannerSection banners={bannersData || []} isLoading={showBannersLoading} />
        
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6 mt-8">
          <div className="flex-1 overflow-hidden col-span-4 max:col-span-5">
            {!showFeaturedProductsLoading ? (featuredProductsData || []).map((item, idx) => (
              Array.isArray(item.products) && item.products.length > 0 ? (
                <RecommendationCardsSection
                  key={item.id || idx}
                  cards={item.products}
                  title={item.title || t('home.sections.recommended_cards')}
                  isLoading={false}
                />
              ) : null
            )) : <RecommendationCardsSection isLoading={true} cards={[]} title={''} />}
          </div>
          <div className="col-span-1">
            <HotBrandsSection brands={brandsData || []} isLoading={showBrandsLoading} />
          </div>
        </div>
        <HotCardsSection
          cards={allCards}
          brands={brandOptions}
          categories={categoryOptions}
          selectedBrand={selectedBrand}
          selectedCategory={selectedCategory}
          onBrandChange={handleBrandChange}
          onCategoryChange={handleCategoryChange}
          onSortChange={handleSortChange}
          onLoadMore={handleLoadMore}
          hasMoreCards={hasNextPage}
          isLoading={showCardsLoading}
          isLoadingMore={isFetchingNextPage}
          isFiltersLoading={showBrandsLoading}
          pagesLoaded={pagesLoaded}
        />
        <PenaltyRestrictionDialog
          isOpen={isPenaltyModalOpen}
          mode={penaltyDialogMode}
          onConfirm={handlePenaltyConfirm}
          onSecondary={handlePenaltyDismiss}
          onClose={handlePenaltyDismiss}
        />
      </div>
    </>
  );
};

export default HomePage; 
